<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="state" :validate-on="['submit']" class="h-full">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            Edit User
                        </h3>
                    </div>
                </template>
                <div class="space-y-6">
                    <UFormGroup name="name" label="Name">
                        <UInput v-model="state.name" type="text" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="email" label="Email">
                        <UInput v-model="state.email" type="text" size="xl" disabled />
                    </UFormGroup>
                    <UFormGroup name="password" label="Password">
                        <UInput v-model="state.password" type="text" size="xl" placeholder="Type to change..." />
                    </UFormGroup>
                    <UFormGroup name="role" label="Role">
                        <USelectMenu v-model="state.role" :options="roles" value-attribute="value" size="xl" />
                    </UFormGroup>
                    <UFormGroup v-if="state.role === UserRole.partner" name="partner_id" label="Partner">
                        <UInputMenu
                            v-model="selectedPartner"
                            :loading="searchingPartners"
                            :search="searchPartners"
                            option-attribute="name"
                            by="id"
                            placeholder="Search partners..."
                            size="xl"
                        />
                    </UFormGroup>
                    <UFormGroup v-if="state.role === UserRole.partner && state.partner_id" name="partner_policy_holder" label="Policy Holder">
                        <UInputMenu
                            v-model="state.partner_policy_holder"
                            :loading="searchingPolicyHolders"
                            :search="searchPolicyHolders"
                            placeholder="Search..."
                            size="xl"
                            nullable
                        />
                    </UFormGroup>
                    <UFormGroup v-if="state.role === UserRole.fieldMedic" name="field_post_ids" label="Field Posts">
                        <USelectMenu v-model="state.field_post_ids" :options="fieldPosts" value-attribute="id"
                            option-attribute="name" multiple placeholder="Select posts..." size="xl" />
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white">
                            Cancel
                        </UButton>
                        <UButton type="submit" size="lg" :loading="loading" color="black">
                            Save
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import { UserRole, type User } from '~/types/user'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
    user?: User
}>()

const auth = useAuthStore()

const roles = computed(() => [
    { value: UserRole.admin, label: 'Administrator' },
    { value: UserRole.medicalDirector, label: 'Medical Director' },
    { value: UserRole.doctor, label: 'Doctor' },
    { value: UserRole.operator, label: 'Operator' },
    { value: UserRole.partner, label: 'Partner' },
    { value: UserRole.viewer, label: 'Viewer' },
    { value: UserRole.fieldMedic, label: 'Field Medic' },
].filter(r => r.value !== UserRole.fieldMedic || auth.isEnabled('field_posts') || props.user?.role === UserRole.fieldMedic))

const client = useSupabaseClient()

const form = ref()
const state = ref()
const loading = ref<boolean>(false)
const selectedPartner = ref()
const searchingPartners = ref<boolean>(false)
const searchingPolicyHolders = ref<boolean>(false)
const fieldPosts = ref<{ id: string; name: string }[]>([])

async function fetchFieldPosts() {
    if (!auth.isEnabled('field_posts')) return
    const { data } = await client.from('field_posts').select('id, name').order('name')
    fieldPosts.value = (data ?? []) as { id: string; name: string }[]
}

const schema = z.object({
    name: z.string(),
    email: z.string().email(),
    role: z.nativeEnum(UserRole),
    password: z.string().min(8).optional(),
    partner_id: z.string().optional(),
    partner_policy_holder: z.string().optional(),
    field_post_ids: z.array(z.string()).optional(),
}).refine((metadata) => {
    if (metadata.role === UserRole.partner && !metadata.partner_id) {
        return false
    }
    return true
}, {
    path: ['partner_id']
}).refine((data) => data.role !== UserRole.fieldMedic || (data.field_post_ids?.length ?? 0) > 0, {
    path: ['field_post_ids'],
    message: 'Select at least one post',
})

watch(() => props.open, async (open) => {
    if (open) {
        state.value = {
            name: props.user?.name,
            email: props.user?.email,
            role: props.user?.role,
            password: undefined,
            partner_id: props.user?.metadata?.partner_id,
            partner_policy_holder: props.user?.metadata?.partner_policy_holder,
            field_post_ids: [],
        }

        await fetchFieldPosts()
        if (props.user?.role === UserRole.fieldMedic) {
            const { data } = await client.from('field_post_members').select('post_id').eq('user_id', props.user.id)
            state.value.field_post_ids = (data ?? []).map((m: { post_id: string }) => m.post_id)
        }

        if (props.user?.metadata?.partner_id) {
            selectedPartner.value = await getPartnerById(props.user.metadata.partner_id)
        }
    }
})

watch(() => selectedPartner.value, (partner) => {
    state.value.partner_id = partner.id
})

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    if (props.user) {
        loading.value = true

        try {
            await $fetch(`/api/settings/users/${props.user.id}`, {
                method: 'POST',
                body: event.data
            })

            dismiss(true)
        } catch (error: any) {
            //
        } finally {
            loading.value = false
        }
    }
}

async function getPartnerById(id: string) {
    const { data } = await client.from('partners')
        .select('id, name')
        .is('deleted_at', null)
        .eq('id', id)
        .maybeSingle()

    return data
}

async function searchPartners(q: string) {
    searchingPartners.value = true

    const { data, error } = await client.from('partners')
        .select('id, name')
        .is('deleted_at', null)
        .ilike('name', `%${q}%`)
        .order('name')

    searchingPartners.value = false

    if (data && !error) {    
        return data
    }

    return []
}

async function searchPolicyHolders(q: string) {
    searchingPolicyHolders.value = true

    const { data, error } = await client
        .rpc('get_policy_holder_suggestions', { partial_name: q } as never)
        .returns<{ policy_holder: string }[]>()

    searchingPolicyHolders.value = false

    if (data && !error) {    
        return data.map((t) => t.policy_holder)
    }

    return []
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)

    if (reload) {
        emit('reload', true)
    }
}
</script>